import React, { useState, useEffect } from "react";
import { Dialog, DialogContent } from "../../misc/Dialog";
import { Button } from "../../misc/Button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../../misc/Card";
import { Popover, PopoverContent, PopoverTrigger } from "../../misc/Popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../misc/Select";
import { CalendarIcon, Clock, Save } from "lucide-react";
import { format, isBefore, setHours, setMinutes } from "date-fns";
import CustomDatePicker from "../../misc/CutomDatePicker";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { toast } from "react-toastify";
import CustomToast from "../../misc/CustomToast";
import { postTime } from "../../campaign/types";
import { cn } from "../../../utils/classMerger";
import { FaTimes } from "react-icons/fa";

interface SchedulePostDialogProps {
    isOpen: boolean;
    onClose: () => void;
    post: {
        id: string;
        platform: string;
        scheduledFor?: string;
    };
    onSave: (updatedSchedule: { scheduledFor: string }) => void;
}

const SchedulePostDialog: React.FC<SchedulePostDialogProps> = ({
    isOpen,
    onClose,
    post,
    onSave,
}) => {
    const [date, setDate] = useState<Date>(
        post.scheduledFor ? new Date(post.scheduledFor) : new Date()
    );
    const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);

    // We'll store time in 24-hour format internally.
    const initialHour = post.scheduledFor
        ? new Date(post.scheduledFor).getHours()
        : 0;
    const initialMinute = post.scheduledFor
        ? new Date(post.scheduledFor).getMinutes()
        : 0;
    const [time, setTime] = useState<postTime>({
        hour: initialHour,
        minute: initialMinute,
    });

    const isMobile = useIsMobile(1100);

    useEffect(() => {
        if (post.scheduledFor) {
            const scheduledDate = new Date(post.scheduledFor);
            setDate(scheduledDate);
            setTime({
                hour: scheduledDate.getHours(),
                minute: scheduledDate.getMinutes(),
            });
        }
    }, [post.scheduledFor]);

    // Compute period from time (24-hour format)
    const period = time.hour >= 12 ? "PM" : "AM";

    // Convert 24-hour hour to 12-hour format for the UI
    const displayHour = (() => {
        const h = time.hour % 12;
        return h === 0 ? 12 : h;
    })();

    // Hour and minute options (full range since we won't restrict them now)
    const hourOptions = Array.from({ length: 12 }, (_, i) => i + 1);
    const minuteOptions = Array.from({ length: 60 }, (_, i) => i);

    const handleSave = () => {
        // Adjust the hour based on AM/PM
        let adjustedHour = displayHour;
        if (period === "PM" && displayHour !== 12) {
            adjustedHour += 12;
        } else if (period === "AM" && displayHour === 12) {
            adjustedHour = 0;
        }

        const scheduledDateTime = setHours(
            setMinutes(new Date(date), time.minute),
            adjustedHour
        );

        // Check if the selected time is in the past
        const now = new Date();
        if (isBefore(scheduledDateTime, now)) {
            toast.error(
                <CustomToast
                    message="Cannot schedule in the past."
                    type="error"
                />,
                { autoClose: 3000 }
            );
            return;
        }

        const newScheduledForDate = scheduledDateTime.toISOString();
        onSave({ scheduledFor: newScheduledForDate });
    };

    const handleHourChange = (value: string) => {
        // value is in 12-hour format. We'll store in state as 24-hour format
        const selectedHour12 = parseInt(value, 10);
        let newHour = selectedHour12;
        if (period === "PM" && selectedHour12 !== 12) {
            newHour = selectedHour12 + 12;
        } else if (period === "AM" && selectedHour12 === 12) {
            newHour = 0;
        }
        setTime((prev) => ({ ...prev, hour: newHour }));
    };

    const handleMinuteChange = (value: string) => {
        const newMinute = parseInt(value, 10);
        setTime((prev) => ({ ...prev, minute: newMinute }));
    };

    // Updated handlePeriodChange function
    const handlePeriodChange = (value: string) => {
        const currentDisplayHour = time.hour % 12 === 0 ? 12 : time.hour % 12;
        const newHour =
            value === "AM"
                ? currentDisplayHour === 12
                    ? 0
                    : currentDisplayHour
                : currentDisplayHour === 12
                ? 12
                : currentDisplayHour + 12;
        setTime((prev) => ({ ...prev, hour: newHour }));
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent
                className="relative w-full h-[50%] m-0 flex flex-col items-center justify-center bg-white max-w-2xl p-0 mt-0"
                dark={true}
            >
                <div
                    className={`${
                        isMobile ? "py-0" : "w-full"
                    } m-auto`}
                >
                    <button
                        className="absolute top-4 right-4 text-secondary hover:text-primary"
                        onClick={onClose}
                        aria-label="Close Dialog"
                    >
                        <FaTimes className="h-6 w-6" />
                    </button>

                    <Card className={`w-full mx-auto p-0 mt-0`}>
                        <CardHeader>
                            <CardTitle>Schedule Post</CardTitle>
                            <CardDescription className="text-center">
                                Set the date and time when you want this {post.platform} post to be published.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4 pt-0">
                            <div className="space-y-2">
                                <label className="text-sm font-medium">
                                    Post Time
                                </label>
                                <div
                                    className={`flex items-center space-x-2 ${
                                        isMobile
                                            ? "flex-col space-y-2"
                                            : "flex-row"
                                    }`}
                                >
                                    <Popover
                                        open={isCalendarOpen}
                                        onOpenChange={setIsCalendarOpen}
                                    >
                                        <PopoverTrigger asChild>
                                            <Button
                                                id="open-calendar-btn"
                                                variant="outline"
                                                className={cn(
                                                    "w-[240px] justify-start text-left font-normal",
                                                    !date &&
                                                        "text-muted-foreground"
                                                )}
                                            >
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {date ? (
                                                    format(date, "PPP")
                                                ) : (
                                                    <span>Change date</span>
                                                )}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent
                                            className="w-auto p-0"
                                            align="start"
                                            portalled={false}
                                        >
                                            <CustomDatePicker
                                                date={date}
                                                setDate={setDate}
                                                restriction="month"
                                            />
                                        </PopoverContent>
                                    </Popover>
                                    <div className="flex items-center space-x-2">
                                        <Clock className="h-4 w-4 text-muted-foreground" />
                                        <Select
                                            value={displayHour.toString()}
                                            onValueChange={handleHourChange}
                                        >
                                            <SelectTrigger
                                                className="w-[70px]"
                                                id="hour-select"
                                            >
                                                <SelectValue placeholder="Hour" />
                                            </SelectTrigger>
                                            <SelectContent className="cursor-pointer">
                                                {hourOptions.map((hour) => (
                                                    <SelectItem
                                                        key={hour}
                                                        value={hour.toString()}
                                                    >
                                                        {hour
                                                            .toString()
                                                            .padStart(
                                                                2,
                                                                "0"
                                                            )}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <Select
                                            value={time.minute.toString()}
                                            onValueChange={
                                                handleMinuteChange
                                            }
                                        >
                                            <SelectTrigger
                                                className="w-[70px]"
                                                id="minute-select"
                                            >
                                                <SelectValue placeholder="Min" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {minuteOptions.map(
                                                    (minute) => (
                                                        <SelectItem
                                                            key={minute}
                                                            value={minute.toString()}
                                                        >
                                                            {minute
                                                                .toString()
                                                                .padStart(
                                                                    2,
                                                                    "0"
                                                                )}
                                                        </SelectItem>
                                                    )
                                                )}
                                            </SelectContent>
                                        </Select>
                                        <Select
                                            value={period}
                                            onValueChange={
                                                handlePeriodChange
                                            }
                                        >
                                            <SelectTrigger
                                                className="w-[70px]"
                                                id="am-pm-select"
                                            >
                                                <SelectValue placeholder="AM/PM" />
                                            </SelectTrigger>
                                            <SelectContent id="am-pm-select">
                                                <SelectItem value="AM">
                                                    AM
                                                </SelectItem>
                                                <SelectItem value="PM">
                                                    PM
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                        <CardFooter>
                            <Button
                                id="save-schedule-btn"
                                className="w-full"
                                onClick={handleSave}
                            >
                                <Save className="mr-2 h-4 w-4" />
                                Save Schedule
                            </Button>
                        </CardFooter>
                    </Card>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default SchedulePostDialog;